// Bottom-sheet for in-app feedback. Kind (bug/suggestion/question/other) +
// body + optional screenshot. Writes to Supabase via lib/feedback.ts and
// notifies the founder via Resend (if configured server-side).

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Check, X, Camera, Loader2 } from "lucide-react";
import { submitFeedback, type FeedbackKind } from "../lib/feedback";
import { friendlyError } from "../lib/errors";

interface Props {
  onClose: () => void;
  /** Optional pre-fill — used by "Report incorrect" in MealReview to seed
   *  the textarea with the offending item's name, portion, and macros so
   *  the user just adds a sentence ("yogurt should be ~90 kcal, not 273"). */
  prefillBody?: string;
  /** Optional default kind — "Report incorrect" calls this with "bug". */
  defaultKind?: FeedbackKind;
}

const KINDS: Array<{ value: FeedbackKind; label: string; emoji: string; placeholder: string }> = [
  { value: "bug", label: "Bug", emoji: "🐛", placeholder: "What went wrong? Steps to reproduce help a lot." },
  { value: "suggestion", label: "Suggestion", emoji: "💡", placeholder: "What would you like to see? Be as specific as you can." },
  { value: "question", label: "Question", emoji: "❓", placeholder: "Ask away — I'll get back to you." },
  { value: "other", label: "Other", emoji: "✉️", placeholder: "What's on your mind?" }
];

export default function FeedbackModal({ onClose, prefillBody, defaultKind }: Props) {
  const [kind, setKind] = useState<FeedbackKind>(defaultKind ?? "bug");
  const [body, setBody] = useState(prefillBody ?? "");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Autofocus the body field shortly after mount so the keyboard slides up on iOS.
  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  // Esc to dismiss.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  // Release the preview blob URL when component unmounts or screenshot changes.
  useEffect(() => {
    return () => {
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    };
  }, [screenshotPreview]);

  function pickScreenshot(file: File) {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  }

  function clearScreenshot() {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await submitFeedback({ kind, body, screenshot });
      setSuccess(true);
      // Auto-dismiss after a beat so the user sees the success state.
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  const currentKind = KINDS.find((k) => k.value === kind)!;

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm" onClick={() => !busy && onClose()} />
      <div
        className="fixed inset-x-0 bottom-0 z-50 bg-slate-900 border-t border-slate-800 rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
      >
        <div className="max-w-md md:max-w-2xl mx-auto p-4 space-y-4">
          {/* Drag handle + close */}
          <div className="flex items-center justify-between relative">
            <div className="h-1 w-10 rounded-full bg-slate-700 mx-auto" />
            <button
              type="button"
              onClick={() => !busy && onClose()}
              className="absolute right-0 w-8 h-8 rounded-full text-slate-400 hover:bg-slate-800 flex items-center justify-center"
              aria-label="Close"
              disabled={busy}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {success ? (
            <div className="py-12 text-center space-y-3">
              <Check className="w-12 h-12 text-emerald-400 mx-auto" strokeWidth={2.5} />
              <h2 id="feedback-title" className="text-lg font-semibold text-emerald-400">Got it. Thank you.</h2>
              <p className="text-sm text-slate-400">I read every piece of feedback personally.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h2 id="feedback-title" className="text-lg font-semibold text-slate-100">Send feedback</h2>
                <p className="text-xs text-slate-400 mt-0.5">Goes straight to James. Includes your email so I can reply.</p>
              </div>

              {/* Kind picker */}
              <fieldset>
                <legend className="text-xs text-slate-400 uppercase tracking-wide mb-1.5">Type</legend>
                <div className="grid grid-cols-4 gap-2">
                  {KINDS.map((k) => (
                    <button
                      key={k.value}
                      type="button"
                      onClick={() => setKind(k.value)}
                      aria-pressed={kind === k.value}
                      className={`p-2 rounded-lg border text-xs font-medium ${
                        kind === k.value
                          ? "bg-emerald-950/40 border-emerald-700 text-emerald-100"
                          : "bg-slate-800 border-slate-700 text-slate-300"
                      }`}
                    >
                      <div className="text-base leading-none mb-1" aria-hidden="true">{k.emoji}</div>
                      {k.label}
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Body */}
              <label className="block">
                <span className="sr-only">Feedback details</span>
                <textarea
                  ref={textareaRef}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={currentKind.placeholder}
                  rows={5}
                  maxLength={4000}
                  required
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 resize-y"
                />
                <div className="flex justify-end text-[11px] text-slate-500 mt-1 tabular-nums">
                  {body.length} / 4000
                </div>
              </label>

              {/* Screenshot picker */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) pickScreenshot(f);
                  }}
                  className="hidden"
                />
                {screenshotPreview ? (
                  <div className="relative">
                    <img src={screenshotPreview} alt="Screenshot preview" className="w-full max-h-48 object-contain rounded-lg bg-slate-800 border border-slate-700" />
                    <button
                      type="button"
                      onClick={clearScreenshot}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-slate-950/80 text-slate-300 hover:bg-slate-900 flex items-center justify-center"
                      aria-label="Remove screenshot"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-2.5 rounded-lg border border-dashed border-slate-700 text-slate-400 text-sm flex items-center justify-center gap-2 hover:border-emerald-700 hover:text-emerald-400"
                  >
                    <Camera className="w-4 h-4" />
                    Attach a screenshot (optional)
                  </button>
                )}
              </div>

              {error && (
                <div className="p-2.5 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy || !body.trim()}
                  className="flex-1 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {busy ? "Sending…" : "Send feedback"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
